/**
 * AI Chat Messages
 * Displays the conversation history with streaming support
 */

import { MdPerson, MdAutoAwesome } from "react-icons/md";
import ArtifactRenderer from "./ArtifactRenderer";
import ReactMarkdown from "react-markdown";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const MARKDOWN_COMPONENTS = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="mb-2 list-disc pl-4">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 list-decimal pl-4">{children}</ol>,
  li: ({ children }) => <li className="mb-1">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  code: ({ children }) => (
    <code className="rounded bg-surface-raised px-1 py-0.5 text-sm">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-control bg-surface-raised p-3 text-sm">
      {children}
    </pre>
  ),
};

const ASSISTANT_AVATAR = "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200";

const MessageAvatar = ({ isUser, isError }) => (
  <div
    aria-hidden="true"
    className={cx(
      "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full",
      isUser ? "bg-primary text-primary-fg" : isError ? "bg-danger-subtle text-danger-fg" : ASSISTANT_AVATAR
    )}
  >
    {isUser ? <MdPerson className="h-5 w-5" /> : <MdAutoAwesome className="h-4 w-4" />}
  </div>
);

// Parse ```chart / ```table blocks out of message content
const parseArtifactsFromContent = (content) => {
  const artifacts = [];
  let cleanContent = content;

  const extract = (type, regex) => {
    let match;
    while ((match = regex.exec(content)) !== null) {
      try {
        const data = JSON.parse(match[1].trim());
        artifacts.push({ id: `${type}-${Date.now()}-${artifacts.length}`, type, data });
        cleanContent = cleanContent.replace(match[0], "");
      } catch (e) {
        // Keep malformed blocks as-is
      }
    }
  };

  extract("chart", /```chart\n([\s\S]*?)```/g);
  extract("table", /```table\n([\s\S]*?)```/g);

  return { cleanContent: cleanContent.trim(), artifacts };
};

const AIChatMessages = ({
  messages,
  streamingContent = "",
  isStreaming = false,
  onSaveArtifact,
}) => (
  <div className="space-y-4">
    {messages.map((message, index) => {
      const isUser = message.role === "user";
      const isError = message.isError;
      const { cleanContent, artifacts } = parseArtifactsFromContent(message.content || "");
      // Backend-provided artifacts win over ones parsed from the text
      const allArtifacts = message.artifacts?.length > 0 ? message.artifacts : artifacts;

      return (
        <div key={index} className={cx("flex gap-3", isUser && "flex-row-reverse")}>
          <MessageAvatar isUser={isUser} isError={isError} />

          <div
            className={cx(
              "max-w-[85%] rounded-card px-4 py-3 text-sm",
              isUser
                ? "bg-primary text-primary-fg"
                : isError
                ? "bg-danger-subtle text-danger-fg"
                : "bg-surface-subtle text-fg"
            )}
          >
            {cleanContent && (
              <div className="break-words">
                <ReactMarkdown components={MARKDOWN_COMPONENTS}>{cleanContent}</ReactMarkdown>
              </div>
            )}

            {allArtifacts.length > 0 && (
              <div className="mt-3 space-y-3">
                {allArtifacts.map((artifact) => (
                  <ArtifactRenderer key={artifact.id} artifact={artifact} onSave={onSaveArtifact} />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}

    {isStreaming && (
      <div className="flex gap-3">
        <MessageAvatar />
        <div className="max-w-[85%] rounded-card bg-surface-subtle px-4 py-3 text-sm text-fg">
          {streamingContent ? (
            <div className="break-words">
              <ReactMarkdown>{streamingContent}</ReactMarkdown>
            </div>
          ) : (
            <div role="status" className="flex items-center gap-2 py-1">
              <span className="sr-only">Atrium AI is typing</span>
              {["-0.3s", "-0.15s", "0s"].map((delay) => (
                <span
                  key={delay}
                  aria-hidden="true"
                  className="inline-block h-3 w-3 rounded-full bg-accent motion-safe:animate-bounce"
                  style={{ animationDelay: delay }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

export default AIChatMessages;
